import React, { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from "react";
import { Modal, OverlayTrigger, Popover } from "react-bootstrap";
import { format } from "date-fns";
import { formatInTimeZone, fromZonedTime, getTimezoneOffset } from "date-fns-tz";
import { useParams } from "react-router-dom";
import { resolveIanaZone } from "../../helper/momentTimezoneMapping";
import { Datepicker } from "@mobiscroll/react";
import { AppointmentMode, AppointmentRescheduledReason, AppointmentType, SaveAppointmentRequest, UpdateAppointmentData } from "appointment-experience-client";
import { PatientPersonalInfo } from "patients-experience-client";
import AsyncTypeahead, { DataListType } from "../../components/AsyncTypeahead";
import { ToasterObject } from "./interface-utils";
import { createPatientApi } from "../../api-helpers/PatientApiHelper";
import { createAppointmentApi } from "../../api-helpers/AppointmentApiHelper";
import { createUsersApi } from "../../api-helpers/UserApiHelper";
import "../../assets/scss/custom/appointment-scheduler.scss";
import PhoneLink from "../../components/PhoneLink";
import { useAppointmentActivityLogger } from "../../hooks/useAppointmentActivityLogger";
import {
  AppointmentActivityAction,
  AppointmentActivityType,
} from "../../constants/AppointmentActivity";

const patientApi = createPatientApi();
const appointmentApi = createAppointmentApi();
const usersApi = createUsersApi();

type Reschedule = {
  isSchedule: boolean;
  appointmentCategory: string;
};

interface SelectedPatientDetails {
  name: string;
  dob: string;
  email: string;
  phone: string;
  timeZone: string;
}

interface AppointmentSchedulerProps {
  appointmentId?: string;
  patientId?: string;
  patient?: PatientPersonalInfo;
  setOpenToaster: (t: ToasterObject) => void;
  getAllAppointments?: () => void;
  isReschedule?: Reschedule;
  confirmType?: string;
  confirmPageAction?: (action: any) => void;
  offCanvasShow?: boolean;
  offCanvasComp?: string;
  setOffCanvasShow: Dispatch<SetStateAction<boolean>>;
  setOffCanvasComp: Dispatch<SetStateAction<string>>;
  loadAppointmentDetails?: () => void;
  reloadGrid?: () => void;
  rescheduleStateId?: string;
  initialTypeId?: string;
  initialModeId?: string;
  initialCoachId?: string;
}

interface SlotInfo {
  label: string;
  coachIds: string[];              // fully available (duration + buffers clear)
  bufferBlockedCoachIds: string[]; // available if buffers ignored (duration clear, buffer conflicts)
  disabled: boolean;               // true when no coach is available at this slot
  isCurrent?: boolean;             // true when this slot is the in-progress block on today
}

interface FrozenPanel {
  schedules: any[];
  patientAvailabilityView: string;
  anchor: Date | null;
  coachEmailToId: Record<string, string>;
  displayCoachId: string;
}

const FROZEN_PANEL_DEFAULT: FrozenPanel = {
  schedules: [],
  patientAvailabilityView: "",
  anchor: null,
  coachEmailToId: {},
  displayCoachId: "",
};

const ALL_COACHES = "__all__";
const AVATAR_COLORS = ["#5B9BD5", "#337E89", "#ED7D31", "#6C757D", "#062740"];

// Minutes per interval for the availabilityView string returned by Graph's getSchedule.
const AVAILABILITY_VIEW_INTERVAL = 30;

const SLOT_STATUS = {
  FREE:               "0",
  TENTATIVE:          "1",
  BUSY:               "2",
  OUT_OF_OFFICE:      "3",
  WORKING_ELSEWHERE:  "4",
} as const;

const LOCAL_TZ_ABBR =
  new Intl.DateTimeFormat("en-US", { timeZoneName: "short" })
    .formatToParts(new Date())
    .find((p) => p.type === "timeZoneName")?.value ?? "";

/** Returns an IANA zone identifier. Passes through IANA strings; maps Windows names via the lookup table. */
function toIana(tz: string): string | undefined {
  if (!tz) return undefined;
  if (tz.includes("/")) return tz;  // already IANA ("America/New_York")
  return resolveIanaZone(tz);       // Windows name → IANA via mapping
}

/** Minutes the given IANA zone is ahead (+) or behind (−) the browser. DST-aware. */
function computePatientDelta(ianaZone: string, date: Date): number {
  const patientOffsetMs = getTimezoneOffset(ianaZone, date);
  const browserOffsetMs = -date.getTimezoneOffset() * 60_000;
  return (patientOffsetMs - browserOffsetMs) / 60_000;
}

/** Parses a "H:MM AM/PM" label into 24-hour hours + minutes. Returns null if the label doesn't match. */
function parseSlotLabel(label: string): { hours: number; minutes: number } | null {
  const match = label.match(/(\d+):(\d+)\s+(AM|PM)/);
  if (!match) return null;
  let hours = parseInt(match[1]);
  const minutes = parseInt(match[2]);
  if (match[3] === "PM" && hours !== 12) hours += 12;
  if (match[3] === "AM" && hours === 12) hours = 0;
  return { hours, minutes };
}

/** Shifts a "H:MM AM/PM" label from one IANA zone to another. */
function shiftTimeLabel(timeLabel: string, fromIana: string, toIana: string, date: Date): string {
  const parsed = parseSlotLabel(timeLabel);
  if (!parsed) return timeLabel;
  const utc = fromZonedTime(
    new Date(date.getFullYear(), date.getMonth(), date.getDate(), parsed.hours, parsed.minutes),
    fromIana
  );
  return formatInTimeZone(utc, toIana, "h:mm a");
}

/** Parses an Outlook "HH:MM:SS" working hours string into minutes since midnight. */
function parseWorkingHoursToMins(timeStr: string | undefined): number | null {
  if (!timeStr) return null;
  const parts = timeStr.split(":");
  if (parts.length < 2) return null;
  const h = parseInt(parts[0], 10);
  const m = parseInt(parts[1], 10);
  if (isNaN(h) || isNaN(m)) return null;
  return h * 60 + m;
}

/** Converts "HH:MM:SS" working hours string → "9:00 AM" display format. */
function isSlotAvailable(status: string | undefined, allowed: Set<string>): boolean {
  return status !== undefined && allowed.has(status);
}

function formatWorkHoursTime(timeStr: string | undefined): string {
  const mins = parseWorkingHoursToMins(timeStr);
  if (mins === null) return "";
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  const period = h >= 12 ? "PM" : "AM";
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${String(m).padStart(2, "0")} ${period}`;
}

const DAY_ORDER = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
const DAY_ABBR: Record<string, string> = {
  monday: "Mon", tuesday: "Tue", wednesday: "Wed", thursday: "Thu",
  friday: "Fri", saturday: "Sat", sunday: "Sun",
};

const getInitials = (name: string) => {
  const parts = name.trim().split(/\s+/);
  return parts.length >= 2
    ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    : name.slice(0, 2).toUpperCase();
};

const AppointmentScheduler = ({
  appointmentId,
  patientId: patientIdProp,
  patient: patientProp,
  setOffCanvasShow,
  setOpenToaster,
  reloadGrid,
  loadAppointmentDetails,
  rescheduleStateId,
  initialTypeId,
  initialModeId,
  initialCoachId,
}: AppointmentSchedulerProps) => {
  // patientId from URL params (e.g. /patients/:patientId/...) takes lower precedence than explicit prop
  const { patientId: patientIdParam } = useParams<{ patientId?: string }>();
  const resolvedPatientId = patientIdProp ?? patientIdParam ?? null;

  // ── Patient ──────────────────────────────────────────────────────────────
  const [patientOptions, setPatientOptions] = useState<DataListType[]>([]);
  const [selectedPatientId, setSelectedPatientId] = useState<string | null>(null);
  const [patientDetails, setPatientDetails] = useState<SelectedPatientDetails | null>(null);

  const [isBooking, setIsBooking] = useState(false);
  const logAppointmentActivity = useAppointmentActivityLogger();
  const appointmentCategoryDbIdRef = useRef<string>("");
  // Holds the coach ID to apply once eligibleCoaches loads (reschedule pre-fill).
  const pendingCoachIdRef = useRef<string>(initialCoachId ?? "");

  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Set to true when a new coach is selected so the Outlook effect knows to
  // auto-select the first available day once the schedule loads.
  const autoSelectRef = useRef(false);

  // ── Tentative event filtering ────────────────────────────────────────────
  const [allowTentative, setAllowTentative] = useState(false);

  const allowedStatuses = useMemo(() => {
    const s = new Set<string>([SLOT_STATUS.FREE]);
    if (allowTentative) s.add(SLOT_STATUS.TENTATIVE);
    return s;
  }, [allowTentative]);

  // ── Appointment meta ──────────────────────────────────────────────────────
  const [types, setTypes] = useState<AppointmentType[]>([]);
  const [modes, setModes] = useState<AppointmentMode[]>([]);
  const [metaLoading, setMetaLoading] = useState(false);
  const [appointmentTypeId, setAppointmentTypeId] = useState(initialTypeId ?? "");
  const [appointmentModeId, setAppointmentModeId] = useState(initialModeId ?? "");
  const [coachId, setCoachId] = useState("");
  const [coachesForPatient, setCoachesForPatient] = useState<any[]>([]);
  const [coachesForType, setCoachesForType] = useState<any[]>([]);
  const [coachesForPatientLoading, setCoachesForPatientLoading] = useState(false);
  const [coachesForTypeLoading, setCoachesForTypeLoading] = useState(false);

  const coachLoading = coachesForPatientLoading || coachesForTypeLoading;

  const eligibleCoaches = useMemo(() => {
    if (!coachesForPatient.length || !coachesForType.length) return [];
    return coachesForType.filter((c) =>
      coachesForPatient.some((p) => p.key === c.key)
    );
  }, [coachesForPatient, coachesForType]);

  // ── Outlook / calendar ───────────────────────────────────────────────────
  const [coachDetailsById, setCoachDetailsById] = useState<Record<string, { email: string; timezone: string; bio: string; phone: string }>>({});
  const coachEmailToId = useMemo(() =>
    Object.entries(coachDetailsById).reduce<Record<string, string>>((acc, [id, d]) => {
      if (d.email) acc[d.email] = id;
      return acc;
    }, {}),
    [coachDetailsById]);
  const coachEmails = useMemo(() => Object.keys(coachEmailToId), [coachEmailToId]);
  const [selectedMonthRange, setSelectedMonthRange] = useState(() => {
    const now = new Date();
    return {
      start: new Date(now.getFullYear(), now.getMonth(), 1),
      // Extend 14 days into the next month so overflow days shown on the calendar
      // grid are covered. A fixed 6-week grid can show up to 14 next-month days
      // (worst case: 28-day February starting on Sunday → 42 - 28 - 0 = 14).
      end: new Date(now.getFullYear(), now.getMonth() + 1, 14),
    };
  });
  const [calendarMarkers, setCalendarMarkers] = useState<{ invalid: any[]; available: any[] }>({ invalid: [], available: [] });
  const [outlookSchedules, setOutlookSchedules] = useState<any[]>([]);
  const [patientAvailabilityView, setPatientAvailabilityView] = useState<string>("");
  const [outlookLoading, setOutlookLoading] = useState(false);

  // ── Calendar / slots ──────────────────────────────────────────────────────
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  // Snapshot of the outlook schedules + month anchor at the moment the user picks a date.
  // Decouples slot computation from month navigation so the right pane stays stable
  // while the calendar advances to a different month.
  const [frozenPanel, setFrozenPanel] = useState<FrozenPanel>(FROZEN_PANEL_DEFAULT);
  const [timeOfDay, setTimeOfDay] = useState<"morning" | "afternoon">("morning");
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);
  const [showCurrentSlotConfirm, setShowCurrentSlotConfirm] = useState(false);
  const [assignedCoachId, setAssignedCoachId] = useState<string | null>(null);

  const today = new Date().toDateString();

  // ── Patient timezone derivations ─────────────────────────────────────────
  const patientIana = useMemo(
    () => toIana(patientDetails?.timeZone ?? ""),
    [patientDetails?.timeZone]
  );

  const patientTzAbbr = useMemo(() => {
    if (!patientIana) return LOCAL_TZ_ABBR;
    return formatInTimeZone(selectedDate ?? new Date(), patientIana, "zzz");
  }, [patientIana, selectedDate]);

  // Minutes patient TZ is ahead (+) or behind (−) the browser for the selected date.
  // Must use selectedDate (not frozenAnchor) so DST transitions are reflected correctly.
  const patientDelta = useMemo(() => {
    if (!patientIana || !selectedDate) return 0;
    return computePatientDelta(patientIana, selectedDate);
  }, [patientIana, selectedDate]);

  // ── Coach timezone derivations ───────────────────────────────────────────
  const coachIana = useMemo(
    () => assignedCoachId ? toIana(coachDetailsById[assignedCoachId]?.timezone ?? "") : undefined,
    [assignedCoachId, coachDetailsById]
  );

  const coachTzAbbr = useMemo(() => {
    if (!coachIana) return LOCAL_TZ_ABBR;
    return formatInTimeZone(selectedDate ?? new Date(), coachIana, "zzz");
  }, [coachIana, selectedDate]);

  const coachIdToWorkingHours = useMemo(() => {
    const map: Record<string, any> = {};
    for (const s of outlookSchedules) {
      const email = s?.scheduleId;
      if (!email) continue;
      const id = coachEmailToId[email];
      if (id) map[id] = s?.workingHours;
    }
    return map;
  }, [outlookSchedules, coachEmailToId]);

  const availableTimeSlots = useMemo(() => {
    const morning: SlotInfo[] = [];
    const afternoon: SlotInfo[] = [];

    if (!selectedDate || !frozenPanel.anchor) return { morning, afternoon };

    // Day-of-week for the selected date — used to skip coaches who don't work this day.
    const allDaysOfWeek = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
    const selectedDayName = allDaysOfWeek[selectedDate.getDay()];

    // Derived from AVAILABILITY_VIEW_INTERVAL — works at any interval (15, 30, 60 min)
    const SLOTS_PER_DAY = (60 / AVAILABILITY_VIEW_INTERVAL) * 24;

    // Normalize to midnight so a non-midnight time from Mobiscroll's onChange
    // doesn't cause Math.round to jump to the next day's availabilityView data.
    const dateMidnight = new Date(
      selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate()
    );
    // Compute the base slot index directly from the ms difference so DST
    // transitions (23- or 25-hour days) don't shift the index by ±1 hour.
    const msPerSlot = AVAILABILITY_VIEW_INTERVAL * 60_000;
    const dayBaseSlot = Math.round(
      (dateMidnight.getTime() - frozenPanel.anchor.getTime()) / msPerSlot
    );

    const hasAvailabilityView = frozenPanel.schedules.some(
      (s) => typeof s?.availabilityView === "string" && s.availabilityView.length > 0
    );

    // Build per-coach working hours windows in browser-local slot indices.
    // Falls back to 7–19 if working hours are absent; skips TZ shift if timezone unresolvable.
    const selectedApptType = types.find((t) => t.id === appointmentTypeId);
    const durationMins = selectedApptType?.durationMinutes ?? 0;
    const preMins = selectedApptType?.preDurationMinutes ?? 0;
    const postMins = selectedApptType?.postDurationMinutes ?? 0;
    const durationSlots = Math.ceil(durationMins / AVAILABILITY_VIEW_INTERVAL);
    const preSlots = Math.ceil(preMins / AVAILABILITY_VIEW_INTERVAL);
    const postSlots = Math.ceil(postMins / AVAILABILITY_VIEW_INTERVAL);
    console.debug("[AppointmentScheduler] Buffer values:", {
      appointmentTypeId, durationMins, preMins, postMins, durationSlots, preSlots, postSlots,
      rawPre: selectedApptType?.preDurationMinutes, rawPost: selectedApptType?.postDurationMinutes,
    });
    const coachWindows = new Map<string, { slotStart: number; slotEnd: number }>();
    for (const s of frozenPanel.schedules) {
      const scheduleId: string = s?.scheduleId ?? "";
      if (!scheduleId) continue;
      const wh = s?.workingHours;
      const startMins = parseWorkingHoursToMins(wh?.startTime) ?? 7 * 60;
      const endMins   = parseWorkingHoursToMins(wh?.endTime)   ?? 19 * 60;
      const tzRaw = wh?.timeZone;
      const coachTzStr = typeof tzRaw === "string" ? tzRaw : (tzRaw?.name ?? "");
      const coachIanaForSlots = toIana(coachTzStr);
      const coachDelta = coachIanaForSlots
        ? computePatientDelta(coachIanaForSlots, dateMidnight)
        : 0;
      const slotStart = Math.max(0, Math.ceil((startMins - coachDelta) / AVAILABILITY_VIEW_INTERVAL));
      // Last valid slot start = endMins - durationMins (appointment must finish by endMins).
      const slotEnd = Math.min(SLOTS_PER_DAY,
        Math.floor((endMins - durationMins - coachDelta) / AVAILABILITY_VIEW_INTERVAL) + 1);
      coachWindows.set(scheduleId, { slotStart, slotEnd });
    }

    // Union window: widest span across all coaches (so slots from any coach are reachable).
    // Fall back to patient-TZ 7–19 if no schedules.
    const allStarts = Array.from(coachWindows.values()).map((w) => w.slotStart);
    const allEnds   = Array.from(coachWindows.values()).map((w) => w.slotEnd);
    const windowStart = allStarts.length > 0
      ? Math.min(...allStarts)
      : Math.max(0, Math.ceil((7 * 60 - patientDelta) / AVAILABILITY_VIEW_INTERVAL));
    const windowEnd = allEnds.length > 0
      ? Math.max(...allEnds)
      : Math.min(SLOTS_PER_DAY, Math.ceil((19 * 60 - patientDelta) / AVAILABILITY_VIEW_INTERVAL));

    // If the selected date is today, skip slots that have already passed.
    // Math.floor keeps the in-progress block selectable so a same-block cancel/reschedule still works.
    const now = new Date();
    const isToday = selectedDate.toDateString() === now.toDateString();
    const nowSlot = isToday
      ? Math.floor((now.getHours() * 60 + now.getMinutes()) / AVAILABILITY_VIEW_INTERVAL)
      : 0;

    for (let slotWithinDay = Math.max(windowStart, nowSlot); slotWithinDay < windowEnd; slotWithinDay++) {
      const baseIndexForSlot = dayBaseSlot + slotWithinDay;

      // Patient conflict check — marks slot as unavailable if patient isn't free for the full duration.
      let patientBlocked = false;
      if (frozenPanel.patientAvailabilityView) {
        for (let i = 0; i < durationSlots; i++) {
          if (frozenPanel.patientAvailabilityView[baseIndexForSlot + i] !== SLOT_STATUS.FREE) { patientBlocked = true; break; }
        }
      }

      // Collect which coaches are free at this slot and within their own working hours window.
      // Two-pass check: first verify all duration slots are free, then check buffer slots.
      const freeCoachIds: string[] = [];
      const bufferBlockedCoachIds: string[] = [];

      if (!patientBlocked) {
        if (hasAvailabilityView) {
          for (const s of frozenPanel.schedules) {
            const coachDays: string[] = (s?.workingHours?.daysOfWeek ?? []).map((d: string) => d.toLowerCase());
            if (coachDays.length > 0 && !coachDays.includes(selectedDayName)) continue;
            const view: string = s?.availabilityView ?? "";
            if (!view) continue;
            const win = coachWindows.get(s.scheduleId ?? "");
            if (win && (slotWithinDay < win.slotStart || slotWithinDay >= win.slotEnd)) continue;

            const baseIndex = dayBaseSlot + slotWithinDay;

            // Pass 1 — Duration check: all slots within the appointment duration must be available.
            let durationFree = true;
            for (let i = 0; i < durationSlots; i++) {
              if (!isSlotAvailable(view[baseIndex + i], allowedStatuses)) { durationFree = false; break; }
            }
            if (!durationFree) continue;

            // Pass 2 — Buffer check: pre/post buffer slots should be available.
            let bufferFree = true;
            for (let i = -preSlots; i < 0; i++) {
              const idx = baseIndex + i;
              if (idx < 0 || idx >= view.length) continue; // outside view = OK
              if (!isSlotAvailable(view[idx], allowedStatuses)) { bufferFree = false; break; }
            }
            if (bufferFree) {
              for (let i = durationSlots; i < durationSlots + postSlots; i++) {
                const idx = baseIndex + i;
                if (idx < 0 || idx >= view.length) continue;
                if (!isSlotAvailable(view[idx], allowedStatuses)) { bufferFree = false; break; }
              }
            }

            const coachId = frozenPanel.coachEmailToId[s.scheduleId] ?? "";
            if (!coachId) continue;
            if (bufferFree) freeCoachIds.push(coachId);
            else bufferBlockedCoachIds.push(coachId);
          }
        } else {
          freeCoachIds.push(...Object.values(frozenPanel.coachEmailToId)); // no availabilityView → all coaches free
        }
      }

      // Always emit the slot — disabled when no coach is available.
      const patientTotalMinutes = slotWithinDay * AVAILABILITY_VIEW_INTERVAL + patientDelta;
      const hour   = Math.floor(patientTotalMinutes / 60);
      const minute = patientTotalMinutes % 60;
      const displayHour = hour % 12 === 0 ? 12 : hour % 12;
      const period = hour < 12 ? "AM" : "PM";
      const label  = `${displayHour}:${minute.toString().padStart(2, "0")} ${period}`;
      const disabled = freeCoachIds.length === 0;
      const isCurrent = isToday && slotWithinDay === nowSlot;
      (hour < 12 ? morning : afternoon).push({ label, coachIds: freeCoachIds, bufferBlockedCoachIds, disabled, isCurrent });
    }

    return { morning, afternoon };
  }, [selectedDate, frozenPanel, patientDelta, appointmentTypeId, types, allowedStatuses]);

  // Auto-select the relevant time-of-day tab when only one has available (non-disabled) slots.
  useEffect(() => {
    const { morning, afternoon } = availableTimeSlots;
    const hasMorning = morning.some((s) => !s.disabled);
    const hasAfternoon = afternoon.some((s) => !s.disabled);
    if (hasMorning && !hasAfternoon) setTimeOfDay("morning");
    else if (hasAfternoon && !hasMorning) setTimeOfDay("afternoon");
  }, [availableTimeSlots]);

  const slots: SlotInfo[] = timeOfDay === "morning" ? availableTimeSlots.morning : availableTimeSlots.afternoon;

  // Coaches to display in the avatar stack: only those with at least one
  // available slot in the active time-of-day tab.
  const displayedCoaches = useMemo(() => {
    if (!frozenPanel.displayCoachId) return [];
    const base = frozenPanel.displayCoachId === ALL_COACHES ? eligibleCoaches : eligibleCoaches.filter((c) => c.key === frozenPanel.displayCoachId);
    if (slots.length === 0) return base;
    const withSlots = new Set<string>();
    for (const slot of slots) {
      for (const id of slot.coachIds) withSlots.add(id);
    }
    return base.filter((c) => withSlots.has(c.key));
  }, [frozenPanel, eligibleCoaches, slots]);

  const isRescheduleMode = !!appointmentId;

  const [notes, setNotes] = useState("");
  const [rescheduledReasons, setRescheduledReasons] = useState<AppointmentRescheduledReason[]>([]);
  const [rescheduleReasonId, setRescheduleReasonId] = useState("");

  const selectedType = useMemo(
    () => types.find((t) => t.id === appointmentTypeId) ?? null,
    [types, appointmentTypeId]
  );
  const selectedMode = useMemo(
    () => modes.find((m) => m.id === appointmentModeId) ?? null,
    [modes, appointmentModeId]
  );

  const assignedCoach = useMemo(
    () => eligibleCoaches.find((c) => c.key === assignedCoachId) ?? null,
    [eligibleCoaches, assignedCoachId]
  );

  const assignedCoachColor = useMemo(() => {
    const i = displayedCoaches.findIndex((c) => c.key === assignedCoachId);
    return AVATAR_COLORS[i >= 0 ? i % AVATAR_COLORS.length : 0];
  }, [displayedCoaches, assignedCoachId]);

  const confirmationEndTime = useMemo(() => {
    if (!selectedSlot || !selectedType?.durationMinutes) return null;
    const parsed = parseSlotLabel(selectedSlot);
    if (!parsed) return null;
    const totalMins = parsed.hours * 60 + parsed.minutes + selectedType.durationMinutes;
    const endH = Math.floor(totalMins / 60) % 24;
    const endM = totalMins % 60;
    const displayHour = endH % 12 === 0 ? 12 : endH % 12;
    return `${displayHour}:${endM.toString().padStart(2, "0")} ${endH < 12 ? "AM" : "PM"}`;
  }, [selectedSlot, selectedType]);

  // Coach-timezone equivalents of the selected slot (only relevant when a single coach is selected).
  const coachSlotTime = useMemo(() => {
    if (!selectedSlot || !patientIana || !coachIana) return null;
    return shiftTimeLabel(selectedSlot, patientIana, coachIana, selectedDate ?? new Date());
  }, [selectedSlot, patientIana, coachIana, selectedDate]);

  const coachEndTime = useMemo(() => {
    if (!confirmationEndTime || !patientIana || !coachIana) return null;
    return shiftTimeLabel(confirmationEndTime, patientIana, coachIana, selectedDate ?? new Date());
  }, [confirmationEndTime, patientIana, coachIana, selectedDate]);

  // ── Effects ───────────────────────────────────────────────────────────────

  // Fetch appointment types and modes once on mount, in parallel where possible.
  // Types require the category ID first, so modes and the category lookup run
  // concurrently, then types follow the category result.
  useEffect(() => {
    const fetchMeta = async () => {
      setMetaLoading(true);
      try {
        const [modesRes, categoryRes] = await Promise.all([
          appointmentApi.appointmentControllerGetAppointmentMode(),
          appointmentApi.appointmentControllerGetAppointmentCategory("APP_ONE_ONE"),
        ]);
        setModes(modesRes.data.filter((m) => !m.isDeleted));
        appointmentCategoryDbIdRef.current = categoryRes.data.id;
        const categoryTypesRes = await appointmentApi.appointmentControllerGetAppointmentTypesByCategory(
          categoryRes.data.id,
        );
        console.log(categoryTypesRes.data);
        setTypes(categoryTypesRes.data);
      } catch {
        // leave empty — selects will show only the placeholder option
      } finally {
        setMetaLoading(false);
      }
    };
    fetchMeta();
  }, []);

  // Fetch reschedule reasons (only needed in reschedule mode)
  useEffect(() => {
    if (!appointmentId) return;
    appointmentApi.appointmentControllerGetAppointmentRescheduledReasons()
      .then((res) => setRescheduledReasons(res.data))
      .catch(() => {});
  }, [appointmentId]);

  // Auto-select the original coach once eligibleCoaches populates (reschedule pre-fill).
  // Uses a ref so the desired ID survives the intermediate setCoachId("") resets that
  // the patient/type effects fire while loading.
  useEffect(() => {
    if (!pendingCoachIdRef.current || coachId || !eligibleCoaches.length) return;
    if (eligibleCoaches.some((c) => c.key === pendingCoachIdRef.current)) {
      setCoachId(pendingCoachIdRef.current);
      pendingCoachIdRef.current = "";
    }
  }, [eligibleCoaches]);

  // Coaches eligible for the selected patient (filtered by patient's state/license server-side)
  useEffect(() => {
    if (!selectedPatientId) {
      setCoachesForPatient([]);
      return;
    }
    setCoachesForPatientLoading(true);
    setCoachId("");
    usersApi.usersControllerSearchUsers(selectedPatientId, undefined)
      .then((res) => setCoachesForPatient(res.data as any[]))
      .catch(() => setCoachesForPatient([]))
      .finally(() => setCoachesForPatientLoading(false));
  }, [selectedPatientId]);

  // Reset coach when mode is cleared
  useEffect(() => {
    if (!appointmentModeId) setCoachId("");
  }, [appointmentModeId]);

  // Coaches eligible for the selected appointment type
  useEffect(() => {
    if (!appointmentTypeId) {
      setCoachesForType([]);
      setCoachId("");
      setCalendarMarkers({ invalid: [], available: [] });
            setSelectedDate(null);
      setSelectedSlot(null);
      setAssignedCoachId(null);
      return;
    }
    setCoachesForTypeLoading(true);
    setCoachId("");
    appointmentApi.appointmentControllerGetUsersByAppointmentType(appointmentTypeId)
      .then((res) => setCoachesForType(res.data as any[]))
      .catch(() => setCoachesForType([]))
      .finally(() => setCoachesForTypeLoading(false));
  }, [appointmentTypeId]);

  // Resolve coach email(s) — required for the Outlook schedule fetch. TODO: would be great to have the coaches prefetched here.  This is inefficient
  // For ALL_COACHES, fetches all eligible coaches in parallel (one request per coach)
  // then fires a single batched Outlook request. eligibleCoaches is stable here
  // because both patient and type effects reset coachId to "" before updating it.
  useEffect(() => {
    // Reset all Q2 (calendar), Q3 (timeslots), Q4 (confirmation) state so
    // the three right-hand panels start from scratch when the coach changes.
    setCoachDetailsById({});
    setCalendarMarkers({ invalid: [], available: [] });
    setSelectedDate(null);
    setOutlookSchedules([]);
    setPatientAvailabilityView("");
    setSelectedMonthRange(() => {
      const now = new Date();
      return {
        start: new Date(now.getFullYear(), now.getMonth(), 1),
        end: new Date(now.getFullYear(), now.getMonth() + 1, 14),
      };
    });
    setFrozenPanel(FROZEN_PANEL_DEFAULT);
    setTimeOfDay("morning");
    setSelectedSlot(null);
    setAssignedCoachId(null);
    setNotes("");
    setRescheduleReasonId("");

    if (!coachId) return;

    // Flag the Outlook effect to auto-select the first available day
    autoSelectRef.current = true;
    if (coachId === ALL_COACHES) {
      Promise.all(eligibleCoaches.map((c) => usersApi.usersControllerFindOne(c.key)))
        .then((results) => {
          const details: Record<string, { email: string; timezone: string; bio: string; phone: string }> = {};
          results.forEach((r, i) => {
            const id = eligibleCoaches[i].key;
            details[id] = { email: r.data.email ?? "", timezone: r.data.timezone ?? "", bio: r.data.bio ?? "", phone: r.data.phone ?? "" };
          });
          console.debug("[AppointmentScheduler] all coach details resolved →", Object.keys(details));
          setCoachDetailsById(details);
        })
        .catch((err) => {
          console.error("[AppointmentScheduler] Failed to fetch all coach details:", err);
          setCoachDetailsById({});
        });
      return;
    }
    usersApi.usersControllerFindOne(coachId)
      .then((res) => {
        setCoachDetailsById({ [coachId]: { email: res.data.email ?? "", timezone: res.data.timezone ?? "", bio: res.data.bio ?? "", phone: res.data.phone ?? "" } });
      })
      .catch((err) => {
        console.error("[AppointmentScheduler] Failed to fetch coach details:", err);
        setCoachDetailsById({});
      });
  }, [coachId]);

  // Fetch Outlook free/busy schedule for the visible calendar month.
  // Sends all resolved emails in a single batched request.
  // Working hours are aggregated as a union: a day is "working" if ANY coach works it,
  // so only days where ALL coaches are off get blocked on the calendar.
  useEffect(() => {
    console.debug("[AppointmentScheduler] coachEmails changed →", coachEmails);
    if (!coachEmails.length || !selectedPatientId) return;
    setOutlookLoading(true);

    appointmentApi.appointmentControllerGetCombinedSchedules({
      userEmails: coachEmails,
      startDateTime: selectedMonthRange.start.toISOString(),
      endDateTime: selectedMonthRange.end.toISOString(),
      availabilityViewInterval: AVAILABILITY_VIEW_INTERVAL,
      patientId: selectedPatientId!,
    })
      .then((res) => {
        const schedules = (res.data.coachSchedules ?? []) as any[];
        const patientView = (res.data.patientAvailabilityView ?? "") as string;
        console.debug("[AppointmentScheduler] Combined schedule response:", res.data);
        setOutlookSchedules(schedules);
        setPatientAvailabilityView(patientView);
      })
      .catch((err) => {
        console.error("[AppointmentScheduler] Outlook schedule fetch failed:", err);
      })
      .finally(() => setOutlookLoading(false));
  }, [coachEmails, selectedMonthRange, selectedPatientId]);

  // Process Outlook schedule data into calendar highlights + invalid dates.
  useEffect(() => {
    if (!outlookSchedules.length) return;

    const allDays = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
    const workingDaysUnion = new Set<string>();
    outlookSchedules.forEach((s) => {
      (s?.workingHours?.daysOfWeek ?? []).forEach((d: string) => workingDaysUnion.add(d.toLowerCase()));
    });
    if (workingDaysUnion.size === 0) {
      ["monday", "tuesday", "wednesday", "thursday", "friday"].forEach((d) => workingDaysUnion.add(d));
    }

    const offDays = allDays
      .filter((d) => !workingDaysUnion.has(d))
      .map((d) => d.slice(0, 2).toUpperCase());
    console.debug("[AppointmentScheduler] Working days:", Array.from(workingDaysUnion), "Off days:", offDays);

    const SLOTS_PER_DAY = (60 / AVAILABILITY_VIEW_INTERVAL) * 24; // 24 at 60 min
    const msPerSlot     = AVAILABILITY_VIEW_INTERVAL * 60_000;

    const apptDurationMins = selectedType?.durationMinutes ?? 0;
    const apptPreMins = selectedType?.preDurationMinutes ?? 0;
    const apptPostMins = selectedType?.postDurationMinutes ?? 0;
    const apptDurationSlots = Math.ceil(apptDurationMins / AVAILABILITY_VIEW_INTERVAL);
    const apptPreSlots = Math.ceil(apptPreMins / AVAILABILITY_VIEW_INTERVAL);
    const apptPostSlots = Math.ceil(apptPostMins / AVAILABILITY_VIEW_INTERVAL);

    // Returns whether a day has morning slots, afternoon slots, both, or none —
    // classified in patient-local time so the calendar colours match the time chips.
    const getSlotAvailability = (day: Date): "none" | "morning" | "afternoon" | "both" => {
      const dayName = allDays[day.getDay()];
      // Use ms-to-slot conversion (not day count × slots-per-day) so DST
      // transitions don't shift the index by ±1 hour.
      const dayBaseSlot = Math.round((day.getTime() - selectedMonthRange.start.getTime()) / msPerSlot);
      const dayPatientDelta = patientIana ? computePatientDelta(patientIana, day) : 0;
      let hasMorning = false;
      let hasAfternoon = false;
      const isTodayHighlight = day.toDateString() === todayDate.toDateString();
      const nowSlotHighlight = isTodayHighlight
        ? Math.floor((new Date().getHours() * 60 + new Date().getMinutes()) / AVAILABILITY_VIEW_INTERVAL)
        : 0;
      for (const s of outlookSchedules) {
        const coachDays = (s?.workingHours?.daysOfWeek ?? []).map((d: string) => d.toLowerCase());
        if (coachDays.length > 0 && !coachDays.includes(dayName)) continue;
        const view: string = s?.availabilityView ?? "";
        const wh = s?.workingHours;
        const startMins = parseWorkingHoursToMins(wh?.startTime) ?? 7 * 60;
        const endMins   = parseWorkingHoursToMins(wh?.endTime)   ?? 19 * 60;
        const tzRaw = wh?.timeZone;
        const coachTzStr = typeof tzRaw === "string" ? tzRaw : (tzRaw?.name ?? "");
        const coachIanaForSlots = toIana(coachTzStr);
        const coachDelta = coachIanaForSlots ? computePatientDelta(coachIanaForSlots, day) : 0;
        const wStart = Math.max(0, Math.ceil((startMins - coachDelta) / AVAILABILITY_VIEW_INTERVAL));
        const wEnd = Math.min(SLOTS_PER_DAY,
          Math.floor((endMins - apptDurationMins - coachDelta) / AVAILABILITY_VIEW_INTERVAL) + 1);
        for (let slot = Math.max(wStart, nowSlotHighlight); slot < wEnd; slot++) {
          // When view is empty, treat all working-hours slots as free
          if (view) {
            const baseIndex = dayBaseSlot + slot;

            // Pass 1 — Duration check: all slots within the appointment duration must be available.
            let durationFree = true;
            for (let i = 0; i < apptDurationSlots; i++) {
              if (!isSlotAvailable(view[baseIndex + i], allowedStatuses)) { durationFree = false; break; }
            }
            if (!durationFree) continue;

            // Pass 2 — Buffer check: pre/post buffer slots should be available.
            let bufferFree = true;
            for (let i = -apptPreSlots; i < 0; i++) {
              const idx = baseIndex + i;
              if (idx < 0 || idx >= view.length) continue;
              if (!isSlotAvailable(view[idx], allowedStatuses)) { bufferFree = false; break; }
            }
            if (bufferFree) {
              for (let i = apptDurationSlots; i < apptDurationSlots + apptPostSlots; i++) {
                const idx = baseIndex + i;
                if (idx < 0 || idx >= view.length) continue;
                if (!isSlotAvailable(view[idx], allowedStatuses)) { bufferFree = false; break; }
              }
            }
            if (!bufferFree) continue;

            // Patient availability check — slot must be free for the full duration
            if (patientAvailabilityView) {
              let patientFree = true;
              for (let i = 0; i < apptDurationSlots; i++) {
                if (patientAvailabilityView[baseIndex + i] !== SLOT_STATUS.FREE) { patientFree = false; break; }
              }
              if (!patientFree) continue;
            }
          }

          const patientMins = slot * AVAILABILITY_VIEW_INTERVAL + dayPatientDelta;
          const hour = Math.floor(patientMins / 60);
          if (hour < 12) hasMorning = true;
          else hasAfternoon = true;
          if (hasMorning && hasAfternoon) break;
        }
        if (hasMorning && hasAfternoon) break;
      }
      if (hasMorning && hasAfternoon) return "both";
      if (hasMorning) return "morning";
      if (hasAfternoon) return "afternoon";
      return "none";
    };

    const todayDate = new Date(); todayDate.setHours(0, 0, 0, 0);
    const highlights: any[] = [];
    const bookedWorkingDays: Date[] = [];
    const cursor = new Date(selectedMonthRange.start);

    while (cursor <= selectedMonthRange.end) {
      if (cursor >= todayDate && workingDaysUnion.has(allDays[cursor.getDay()])) {
        const availability = getSlotAvailability(cursor);
        if (availability !== "none") {
          const cssClass = availability === "morning"
            ? "scheduler-available-day scheduler-available-day--morning"
            : availability === "afternoon"
            ? "scheduler-available-day scheduler-available-day--afternoon"
            : "scheduler-available-day";
          highlights.push({ date: new Date(cursor), cellCssClass: cssClass });
        } else {
          bookedWorkingDays.push(new Date(cursor));
        }
      }
      cursor.setDate(cursor.getDate() + 1);
    }

    const recurringRule = offDays.length
      ? [{ recurring: { repeat: "weekly", weekDays: offDays.join(","), interval: 1 } }]
      : [];

    setCalendarMarkers({
      invalid: [...recurringRule, ...bookedWorkingDays.map((d) => ({ date: d }))],
      available: highlights,
    });

    if (autoSelectRef.current && highlights.length > 0) {
      setSelectedDate(highlights[0].date);
      setFrozenPanel({
        schedules: outlookSchedules,
        patientAvailabilityView,
        anchor: selectedMonthRange.start,
        coachEmailToId,
        displayCoachId: coachId,
      });
      autoSelectRef.current = false;
    }
  // selectedMonthRange is intentionally excluded — it changes in lockstep with
  // outlookSchedules (via the fetch effect), so including it here would cause
  // a stale-data flash where processing runs with the new range but old schedules.
  }, [outlookSchedules, patientAvailabilityView, selectedType, patientIana, allowedStatuses]);

  // Pre-populate patient details when a patientId is known on mount
  // (e.g. opening the scheduler from a patient profile page).
  useEffect(() => {
    if (!resolvedPatientId) return;
    setSelectedPatientId(resolvedPatientId);
    if (patientProp) {
      setPatientDetails({
        name: [patientProp.firstName, patientProp.lastName].filter(Boolean).join(" "),
        dob: patientProp.dob ? format(new Date(patientProp.dob), "MM/dd/yyyy") : "",
        email: patientProp.email ?? "",
        phone: patientProp.phone ?? "",
        timeZone: patientProp.timeZone ?? "",
      });
      return;
    }
    const fetchPatient = async () => {
            try {
        const res = await patientApi.patientsControllerGetPatientPersonalInfo(resolvedPatientId);
        setPatientDetails({
          name: [res.data.firstName, res.data.lastName].filter(Boolean).join(" "),
          dob: res.data.dob ? format(new Date(res.data.dob), "MM/dd/yyyy") : "",
          email: res.data.email ?? "",
          phone: res.data.phone ?? "",
          timeZone: (res.data as any).timeZone ?? "",
        });
      } catch {
        // leave fields as muted dashes
      }
    };
    fetchPatient();
    // resolvedPatientId and patientProp are stable for the lifetime of this offcanvas open
  }, []);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, []);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleSlotClick = (slot: SlotInfo) => {
    setSelectedSlot(slot.label);
    const pick = slot.coachIds[Math.floor(Math.random() * slot.coachIds.length)];
    setAssignedCoachId(pick ?? null);
  };

  const handleDateSelect = (e: any) => {
    // Mobiscroll fires onChange with null when navigating months in controlled mode; ignore it.
    // Also ignore events fired on load before a coach has been selected.
    if (e.value == null || !coachId) return;
    setSelectedDate(e.value as Date);
    setSelectedSlot(null);
    setAssignedCoachId(null);
    setFrozenPanel({
      schedules: outlookSchedules,
      patientAvailabilityView,
      anchor: selectedMonthRange.start,
      coachEmailToId,
      displayCoachId: coachId,
    });
  };

  const handlePageChange = (e: any) => {
    const first = new Date(e.firstDay);
    // Resolve the 1st of the displayed month — e.firstDay may land in the
    // previous month's overflow (e.g. grid starts May 31 when viewing June).
    const displayedFirst = first.getDate() === 1
      ? first
      : new Date(first.getFullYear(), first.getMonth() + 1, 1);

    setSelectedMonthRange({
      // Go back 6 days to cover previous-month overflow cells in the grid
      start: new Date(displayedFirst.getFullYear(), displayedFirst.getMonth(), 1 - 6),
      // Extend 14 days past the last day of the displayed month to cover
      end: new Date(displayedFirst.getFullYear(), displayedFirst.getMonth() + 1, 14),
    });
  };

  const handlePatientSearch = (query: string) => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(async () => {
      try {
        const res = await patientApi.patientsControllerSearchPatient(query);
        setPatientOptions((res.data as any[]).map((p) => ({ key: String(p.key), value: p.value })));
      } catch {
        setPatientOptions([]);
      }
    }, 300);
  };

  const handlePatientSelect = async (item: DataListType | null) => {
    setAppointmentTypeId("");
    setAppointmentModeId("");
    setNotes("");
    setFrozenPanel(FROZEN_PANEL_DEFAULT);
    if (!item) {
      setSelectedPatientId(null);
      setPatientDetails(null);
      return;
    }
    setSelectedPatientId(item.key);
        try {
      const res = await patientApi.patientsControllerGetPatientPersonalInfo(item.key);
      setPatientDetails({
        name: item.value,
        dob: res.data.dob ? format(new Date(res.data.dob), "MM/dd/yyyy") : "",
        email: res.data.email ?? "",
        phone: res.data.phone ?? "",
        timeZone: (res.data as any).timeZone ?? "",
      });
    } catch {
      setPatientDetails(null);
    }
  };

  // Swap in a darker CSS class for the currently selected date so it visually
  // stands out from the rest of the available-day highlights.
  const calendarColors = useMemo(() => {
    if (!selectedDate) return calendarMarkers.available;
    return calendarMarkers.available.map((entry) => {
      const d = entry.date as Date;
      if (d.toDateString() === selectedDate.toDateString()) {
        return { ...entry, cellCssClass: `${entry.cellCssClass} scheduler-selected-day` };
      }
      return entry;
    });
  }, [calendarMarkers.available, selectedDate]);

  const handleBookAppointment = async () => {
    if (!selectedSlot || !selectedDate) return;

    const parsed = parseSlotLabel(selectedSlot);
    if (!parsed) return;
    const localDate = new Date(
      selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate(),
      parsed.hours, parsed.minutes
    );
    const time = patientIana ? fromZonedTime(localDate, patientIana) : localDate;

    setIsBooking(true);
    try {
      if (!appointmentId) {
        const booked = await appointmentApi.appointmentControllerSaveAppointment({
          time,
          appointmentCategoryId: appointmentCategoryDbIdRef.current,
          patientIds: [selectedPatientId!],
          email: patientDetails?.email ?? "",
          phone: patientDetails?.phone ?? "",
          appointmentTypeId,
          appointmentModeId,
          primaryCoachUserId: assignedCoachId!,
          isRecurring: false,
          reccuringFrequency: "",
          designerId: "",
          designerStateId: "",
          coachIds: [assignedCoachId!],
          comment: notes,
        } as SaveAppointmentRequest);
        if (booked.data?.id) {
          void logAppointmentActivity({
            patientId: selectedPatientId!,
            activityType: AppointmentActivityType.APPOINTMENT,
            refId: booked.data.id,
            action: AppointmentActivityAction.COMPLETED,
            title: `${selectedType?.name ?? "Appointment"} on ${format(time, "MM/dd/yyyy")}`,
          });
        }
        setOpenToaster({ message: "Appointment booked successfully", show: true, type: "success" });
        setOffCanvasShow(false);
        reloadGrid?.();
      } else {
        await appointmentApi.appointmentControllerUpdateAppointmentById(
          {
            appointmentTime: time,
            appointmentTypeId,
            appointmentModeId,
            duration: selectedType?.durationMinutes,
            comment: notes,
            status: rescheduleStateId,
            rescheduledReason: rescheduleReasonId,
          } as UpdateAppointmentData,
          appointmentId
        );
        setOpenToaster({ message: "Appointment rescheduled successfully", show: true, type: "success" });
        setOffCanvasShow(false);
        loadAppointmentDetails?.();
      }
    } catch (err) {
      const msg = (err as any)?.response?.data?.message?.message?.message ?? "Something went wrong";
      setOpenToaster({ message: msg, show: true, type: "error" });
    } finally {
      setIsBooking(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <>
    <div className="row g-0 pb-4">
      {/* ── Left Column ─────────────────────────────── */}
      <div className="col-lg-6 pe-lg-3 d-lg-flex flex-lg-column">
        {/* Q1: Field Information */}
        <div className="scheduler-quadrant mb-3">

        {/* Patient Name */}
        <div className="mb-2">
          <label className="form-label fw-semibold fs-12">Patient Name</label>
          <AsyncTypeahead
            options={patientOptions}
            callback={handlePatientSearch}
            onSelect={handlePatientSelect}
            placeholder="Search patient..."
            defaultValue={patientDetails?.name}
            unsetId={() => handlePatientSelect(null)}
            disabled={isRescheduleMode}
          />
        </div>

        {/* Email + Phone */}
        <div className="row mb-2">
          <div className="col-12 col-sm-6">
            <label className="form-label fw-semibold fs-12">Email</label>
            {patientDetails?.email ? (
              <a href={`mailto:${patientDetails.email}`} className="form-control fs-12 text-decoration-none d-block text-truncate">
                {patientDetails.email}
              </a>
            ) : (
              <div className="form-control fs-12 text-muted">—</div>
            )}
          </div>
          <div className="col-12 col-sm-6 mt-2 mt-sm-0">
            <label className="form-label fw-semibold fs-12">Phone Number</label>
            <PhoneLink
              phone={patientDetails?.phone}
              className="form-control fs-12 text-decoration-none d-block text-truncate"
              fallback={<div className="form-control fs-12 text-muted">—</div>}
            />
          </div>
        </div>

        {/* Appointment Type + Mode */}
        <div className="row mb-2">
          <div className="col-12 col-sm-6">
            <label className="form-label fw-semibold fs-12">Appointment Type</label>
            <select
              className="form-select fs-12"
              value={appointmentTypeId}
              onChange={(e) => setAppointmentTypeId(e.target.value)}
              disabled={metaLoading || isRescheduleMode}
            >
              <option value="">{metaLoading ? "Loading…" : "Select Type"}</option>
              {types.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
          <div className="col-12 col-sm-6 mt-2 mt-sm-0">
            <label className="form-label fw-semibold fs-12">Mode</label>
            <select
              className="form-select fs-12"
              value={appointmentModeId}
              onChange={(e) => setAppointmentModeId(e.target.value)}
              disabled={metaLoading}
            >
              <option value="">{metaLoading ? "Loading…" : "Select Mode"}</option>
              {modes.map((m) => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Select Coach */}
        <div className="row mb-2">
          <div className="col-12">
            <label className="form-label fw-semibold fs-12">Select Coach</label>
            <select
              className="form-select fs-12"
              value={coachId}
              onChange={(e) => setCoachId(e.target.value)}
              disabled={isRescheduleMode || coachLoading || !appointmentTypeId || !appointmentModeId || eligibleCoaches.length === 0}
            >
              <option value="">
                {coachLoading
                  ? "Loading…"
                  : !appointmentTypeId || !appointmentModeId
                  ? "Select appointment type and mode"
                  : eligibleCoaches.length === 0
                  ? "No eligible coaches"
                  : "Select Coach"}
              </option>
              {!isRescheduleMode && eligibleCoaches.length > 0 && (
                <option value={ALL_COACHES}>All Licensed</option>
              )}
              {eligibleCoaches.map((c) => (
                <option key={c.key} value={c.key}>{c.value}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="form-check mt-2">
          <input
            className="form-check-input"
            type="checkbox"
            id="allow-tentative"
            checked={allowTentative}
            onChange={(e) => setAllowTentative(e.target.checked)}
            disabled={!coachId || outlookLoading}
          />
          <label className="form-check-label fs-12" htmlFor="allow-tentative">
            Allow scheduling over tentative Outlook events
          </label>
        </div>
        </div>{/* end Q1 */}

        {/* Q2: Calendar Picker */}
        <div className="scheduler-quadrant flex-lg-grow-1">
        <h6 className="fw-semibold mb-2">Select Date</h6>
        <div className="position-relative">
          <Datepicker
            controls={["calendar"]}
            calendarType="month"
            pages={1}
            display="inline"
            theme="windows"
            themeVariant="light"
            touchUi={true}
            min={today}
            cssClass="calendars"
            invalid={calendarMarkers.invalid}
            value={selectedDate}
            colors={calendarColors}
            onChange={handleDateSelect}
            onPageChange={handlePageChange}
          />
          {(outlookLoading || !coachId) && (
            <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center rounded"
              style={{ background: "rgba(255,255,255,0.65)", backdropFilter: "blur(2px)", zIndex: 10 }}
            >
              {outlookLoading
                ? <span className="spinner-border spinner-border-sm text-secondary" role="status" />
                : <span className="fs-12 text-muted">Select a coach to view availability</span>
              }
            </div>
          )}
        </div>
        </div>{/* end Q2 */}
      </div>

      {/* ── Right Column ────────────────────────────── */}
      <div className="col-lg-6 ps-lg-3 mt-4 mt-lg-0 d-lg-flex flex-lg-column">
        {/* Q3: Appointment Timeslots */}
        <div className="scheduler-quadrant mb-3">
        {selectedDate ? (
          <div className="position-relative">
            {outlookLoading && !frozenPanel.anchor && (
              <div
                className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center rounded"
                style={{ background: "rgba(255,255,255,0.65)", backdropFilter: "blur(2px)", zIndex: 10 }}
              >
                <span className="spinner-border spinner-border-sm text-secondary" role="status" />
              </div>
            )}
            {/* Morning / Afternoon toggle */}
            <div className="d-flex gap-2 mb-3">
              <button
                type="button"
                className={`scheduler-toggle-pill scheduler-toggle-pill--morning${timeOfDay === "morning" ? " active" : ""}`}
                onClick={() => {
                  setTimeOfDay("morning");
                  setSelectedSlot(null);
                  setAssignedCoachId(null);
                }}
              >
                Morning
              </button>
              <button
                type="button"
                className={`scheduler-toggle-pill scheduler-toggle-pill--afternoon${timeOfDay === "afternoon" ? " active" : ""}`}
                onClick={() => {
                  setTimeOfDay("afternoon");
                  setSelectedSlot(null);
                  setAssignedCoachId(null);
                }}
              >
                Afternoon
              </button>
            </div>

            {/* Date header */}
            <div className="d-flex justify-content-between align-items-center mb-2">
              <p className="fw-semibold mb-0">
                {format(selectedDate, "EEEE MMMM d, yyyy")}
              </p>
              {patientDetails && patientTzAbbr && (
                <span className="fs-12 text-muted">Patient Time Zone ({patientTzAbbr})</span>
              )}
            </div>

            {/* Available coaches */}
            {displayedCoaches.length > 0 && (
              <>
                <p className="fw-semibold fs-12 mb-2" style={{ color: "#5B9BD5" }}>
                  Available Coach(s)
                </p>
                <div className="d-flex align-items-center gap-3 mb-3">
                  <div className="scheduler-avatar-stack">
                    {displayedCoaches.map((coach, i) => {
                      const coachIanaForPopover = toIana(coachDetailsById[coach.key]?.timezone ?? "");
                      const tzAbbr = coachIanaForPopover
                        ? formatInTimeZone(selectedDate!, coachIanaForPopover, "zzz")
                        : LOCAL_TZ_ABBR;
                      const wh = coachIdToWorkingHours[coach.key];
                      const sortedDays: string[] = wh?.daysOfWeek
                        ? [...wh.daysOfWeek].sort(
                            (a: string, b: string) => DAY_ORDER.indexOf(a) - DAY_ORDER.indexOf(b)
                          )
                        : [];
                      const startLabel = formatWorkHoursTime(wh?.startTime);
                      const endLabel = formatWorkHoursTime(wh?.endTime);
                      const patientStart = coachIanaForPopover && patientIana
                        ? shiftTimeLabel(startLabel, coachIanaForPopover, patientIana, selectedDate!)
                        : startLabel;
                      const patientEnd = coachIanaForPopover && patientIana
                        ? shiftTimeLabel(endLabel, coachIanaForPopover, patientIana, selectedDate!)
                        : endLabel;
                      let offsetLabel = "";
                      if (coachIanaForPopover && patientIana) {
                        const coachOffsetMs = getTimezoneOffset(coachIanaForPopover, selectedDate!);
                        const patientOffsetMs = getTimezoneOffset(patientIana, selectedDate!);
                        const diffHours = (coachOffsetMs - patientOffsetMs) / 3_600_000;
                        if (diffHours === 0) {
                          offsetLabel = "Same as patient";
                        } else {
                          const sign = diffHours > 0 ? "+" : "-";
                          const abs = Math.abs(diffHours);
                          offsetLabel = `${sign}${abs}h from patient`;
                        }
                      }
                      const popover = (
                        <Popover>
                          <Popover.Body>
                            <div className="scheduler-coach-popover">
                              <div className="coach-popover-name">{coach.value} ({tzAbbr})</div>
                              <div className="coach-popover-tz">{offsetLabel}</div>
                              {sortedDays.length > 0 && (
                                <>
                                  <hr className="my-2" />
                                  <div className="coach-popover-tz mb-1">Working hours ({patientTzAbbr})</div>
                                  {sortedDays.map((day) => (
                                    <div key={day} className="coach-popover-day-row">
                                      <span className="coach-popover-day-label">{DAY_ABBR[day] ?? day}</span>
                                      <span>{patientStart} – {patientEnd}</span>
                                    </div>
                                  ))}
                                </>
                              )}
                            </div>
                          </Popover.Body>
                        </Popover>
                      );
                      return (
                        <OverlayTrigger
                          key={coach.key}
                          trigger={["hover", "focus"]}
                          placement="top"
                          overlay={popover}
                        >
                          <div
                            className="scheduler-coach-avatar d-inline-flex align-items-center justify-content-center text-white"
                            style={{ backgroundColor: AVATAR_COLORS[i % AVATAR_COLORS.length] }}
                          >
                            {getInitials(coach.value)}
                          </div>
                        </OverlayTrigger>
                      );
                    })}
                  </div>
                  <span className="fs-12 text-muted">
                    {displayedCoaches.map((c) => c.value).join(", ")}
                  </span>
                </div>
              </>
            )}

            {/* Time slot grid */}
            <div className="slot-grid">
              {slots.length > 0 ? slots.map((slot) => (
                <button
                  key={slot.label}
                  type="button"
                  className={`scheduler-time-slot${selectedSlot === slot.label ? " selected" : ""}${slot.disabled ? " scheduler-time-slot--disabled" : ""}${slot.isCurrent ? " scheduler-time-slot--current" : ""}`}
                  onClick={() => handleSlotClick(slot)}
                  disabled={slot.disabled || (outlookLoading && !frozenPanel.anchor)}
                  title={slot.isCurrent ? "This timeslot has already started" : undefined}
                >
                  {slot.label}
                </button>
              )) : (
                <p className="fs-12 text-muted mb-0">No available slots for this time of day.</p>
              )}
            </div>
          </div>
        ) : (
          <div className="scheduler-empty-state">
            <span className="material-symbols-outlined scheduler-empty-state__icon">
              calendar_month
            </span>
            <p className="scheduler-empty-state__label">Select a day to view available times</p>
          </div>
        )}
        </div>{/* end Q3 */}

        {/* Q4: Booking Card */}
        <div className={`scheduler-quadrant${selectedSlot && selectedDate ? " scheduler-quadrant--summary" : ""} flex-lg-grow-1 d-flex flex-column`}>
        {selectedSlot && selectedDate ? (
          <>
            {/* Booking summary — patient/appointment left, coach/clinician right */}
            <div className="scheduler-booking-card mb-3">
              <div className="scheduler-booking-card__header">
                <strong>{selectedType?.name ?? "—"}</strong>
                <span className="text-muted"> &bull; </span>
                <span>{selectedMode?.name ?? "—"}</span>
              </div>
              <div className="d-flex gap-0">

              {/* Left: patient + appointment details */}
              <div className="scheduler-booking-col pe-0">
                <p className="fs-11 text-muted fw-semibold text-uppercase mb-2">Patient</p>
                <div className="d-flex align-items-center fw-semibold fs-14 mb-3" style={{ minHeight: 28 }}>{patientDetails?.name ?? "—"}</div>

                <p className="fs-11 text-muted fw-semibold text-uppercase mb-2">Appointment</p>
                <div className="d-flex align-items-center gap-2 mb-2">
                  <span className="material-symbols-outlined fs-16 text-secondary flex-shrink-0" style={{ width: "1.25rem", textAlign: "center" }}>calendar_month</span>
                  <span className="fs-13">{format(selectedDate, "MMMM d, yyyy")}</span>
                </div>
                <div className="d-flex align-items-start gap-2">
                  <span className="material-symbols-outlined fs-16 text-secondary flex-shrink-0" style={{ width: "1.25rem", textAlign: "center", paddingTop: "1px" }}>schedule</span>
                  <div className="fs-13">
                    <span>{selectedSlot}{confirmationEndTime && ` – ${confirmationEndTime}`}{patientTzAbbr && ` ${patientTzAbbr}`}</span>
                    {coachIana && (
                      <p className="text-muted fs-12 mb-0">
                        {coachSlotTime}{coachEndTime && ` – ${coachEndTime}`}{coachTzAbbr && ` ${coachTzAbbr}`} (Coach)
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Divider */}
              <div className="scheduler-card-divider" />

              {/* Right: coach / clinician details */}
              <div className="scheduler-clinician-info">
                <p className="fs-11 text-muted fw-semibold text-uppercase mb-2">Coach</p>
                <div className="d-flex align-items-center gap-2 mb-3">
                  <div
                    className="scheduler-coach-avatar d-inline-flex align-items-center justify-content-center text-white flex-shrink-0"
                    style={{ backgroundColor: assignedCoachColor, width: 28, height: 28, fontSize: "0.7rem" }}
                  >
                    {assignedCoach ? getInitials(assignedCoach.value) : "?"}
                  </div>
                  <p className="fw-semibold mb-0 fs-14">{assignedCoach?.value ?? "—"}</p>
                </div>

                {assignedCoachId && coachDetailsById[assignedCoachId]?.phone && (
                  <>
                    <div className="d-flex align-items-center gap-2 mb-3">
                      <span className="material-symbols-outlined fs-16 text-secondary flex-shrink-0">phone</span>
                      <PhoneLink phone={coachDetailsById[assignedCoachId].phone} className="fs-13 a-link a-underline text-info" />
                    </div>
                  </>)}

                {assignedCoachId && coachDetailsById[assignedCoachId]?.bio && (
                  <>
                    <p className="fs-11 text-muted fw-semibold text-uppercase mb-1">Bio</p>
                    <div className="scheduler-clinician-about">
                      <p className="fs-12 mb-0" style={{ whiteSpace: "pre-wrap" }}>{coachDetailsById[assignedCoachId].bio}</p>
                    </div>
                  </>
                )}
              </div>
              </div>{/* end columns */}
            </div>{/* end scheduler-booking-card */}

            {/* Reschedule Reason */}
            {isRescheduleMode && (
              <div className="mb-3">
                <label className="form-label fw-semibold fs-12">
                  Reschedule Reason <span className="text-danger">*</span>
                </label>
                <select
                  className="form-select fs-12"
                  value={rescheduleReasonId}
                  onChange={(e) => setRescheduleReasonId(e.target.value)}
                  required
                >
                  <option value="">Select Reason</option>
                  {rescheduledReasons.map((r) => (
                    <option key={r.id} value={r.id}>{r.reasonName}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Notes */}
            <div className="mb-3">
              <label className="form-label fw-semibold fs-12">Notes (optional)</label>
              <textarea
                className="form-control fs-12 scheduler-notes-input"
                rows={3}
                placeholder="Add any notes for this appointment..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>

            {/* Book Appointment button */}
            <button
              type="button"
              className="btn btn-secondary rounded-pill w-100 fs-13 mt-auto"
              onClick={() => {
                const selected = [...availableTimeSlots.morning, ...availableTimeSlots.afternoon]
                  .find((s) => s.label === selectedSlot);
                if (selected?.isCurrent) setShowCurrentSlotConfirm(true);
                else handleBookAppointment();
              }}
              disabled={
                isBooking ||
                !selectedPatientId ||
                !appointmentTypeId ||
                !appointmentModeId ||
                !assignedCoachId ||
                !selectedSlot ||
                !selectedDate ||
                (isRescheduleMode && !rescheduleReasonId)
              }
            >
              {isBooking
                ? <span className="spinner-border spinner-border-sm" role="status" />
                : "Confirm Appointment"}
            </button>
          </>
        ) : (
          <div className="scheduler-empty-state flex-grow-1">
            <span className="material-symbols-outlined scheduler-empty-state__icon">
              event_available
            </span>
            <p className="scheduler-empty-state__label">Select a time slot to preview your booking</p>
          </div>
        )}
        </div>{/* end Q4 */}
      </div>
    </div>
    <Modal
      show={showCurrentSlotConfirm}
      onHide={() => setShowCurrentSlotConfirm(false)}
      centered
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header closeButton>
        <Modal.Title>Book in-progress timeslot?</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        The {selectedSlot ?? ""} timeslot has already started. Do you want to proceed?
      </Modal.Body>
      <Modal.Footer className="justify-content-between">
        <button
          type="button"
          className="btn btn-outline-secondary rounded-pill px-6 my-0"
          onClick={() => setShowCurrentSlotConfirm(false)}
        >
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-danger rounded-pill px-6 my-0"
          onClick={() => {
            setShowCurrentSlotConfirm(false);
            handleBookAppointment();
          }}
        >
          Confirm
        </button>
      </Modal.Footer>
    </Modal>
    </>
  );
};

export default AppointmentScheduler;
